import { useState } from "react";
import { RiskMitigationItem, RiskMitigationPlanStatus } from "./RiskMitigationOverview.types";

// Only a Completed plan can be promoted, so selection is restricted to eligible rows: ineligible rows can't be
// toggled and "select all" acts on the eligible page items only. The backend re-validates and skips anything
// ineligible as a safety net.
const isEligible = (item: RiskMitigationItem) => item.status === RiskMitigationPlanStatus.Completed;

// Bulk-promote selection lives above both the tab-row entry button (in RiskArea) and the mitigation table, so
// the button and the table share one source of truth. Selection persists across pages/filters because it is
// keyed by planId, independent of the currently paged result.
export function useBulkPromoteSelection() {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<Map<string, RiskMitigationItem>>(new Map());

    const enterSelectionMode = () => setIsSelectionMode(true);
    const exitSelectionMode = () => {
        setIsSelectionMode(false);
        setIsConfirmOpen(false);
        setSelectedItems(new Map());
    };

    // The confirm dialog is rendered by the overview (it owns the promote mutation), but the bulk Promote
    // button lives in the tab row next to the selection toggle, so the open-state is shared here.
    const openConfirm = () => setIsConfirmOpen(true);
    const closeConfirm = () => setIsConfirmOpen(false);

    const isSelected = (item: RiskMitigationItem) => selectedItems.has(item.planId);

    const toggleItem = (item: RiskMitigationItem) => {
        if (!isEligible(item)) return;
        setSelectedItems((current) => {
            const next = new Map(current);
            if (next.has(item.planId)) next.delete(item.planId);
            else next.set(item.planId, item);
            return next;
        });
    };

    const onSelectAllChanged = (pageItems: Array<RiskMitigationItem>) => {
        const eligible = pageItems.filter(isEligible);
        setSelectedItems((current) => {
            const next = new Map(current);
            const allEligibleSelected = eligible.every((item) => next.has(item.planId));
            eligible.forEach((item) => {
                if (allEligibleSelected) next.delete(item.planId);
                else next.set(item.planId, item);
            });
            return next;
        });
    };

    const getHeaderCheckBoxState = (pageItems: Array<RiskMitigationItem>): "true" | "false" | "indeterminate" => {
        const eligible = pageItems.filter(isEligible);
        const selectedOnPage = eligible.filter((item) => selectedItems.has(item.planId)).length;
        if (selectedOnPage === 0) return "false";
        return selectedOnPage === eligible.length ? "true" : "indeterminate";
    };

    return {
        isSelectionMode,
        enterSelectionMode,
        exitSelectionMode,
        isConfirmOpen,
        openConfirm,
        closeConfirm,
        isSelected,
        toggleItem,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        selectedItems: Array.from(selectedItems.values()),
        selectedCount: selectedItems.size,
    };
}

export type BulkPromoteSelection = ReturnType<typeof useBulkPromoteSelection>;
